import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Slider,
  Stack,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import { pagePrimaryButtonSx } from "../../styles/pageModernStyles";

const pulseSoft = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(13, 148, 136, 0.35); }
  50% { transform: scale(1.03); box-shadow: 0 0 0 10px rgba(13, 148, 136, 0); }
`;

/**
 * Inpaint: pinta máscara blanca sobre la foto → la IA regenera solo esa zona.
 * Negro = conservar. Blanco = regenerar con el prompt.
 */
export default function MoodboardInpaintDialog({
  open,
  onClose,
  busy = false,
  imageUrl = "",
  onSubmit,
}) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [prompt, setPrompt] = useState("");
  const [brush, setBrush] = useState(28);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setReady(false);
      setPrompt("");
      return undefined;
    }
    if (!imageUrl) {
      setReady(false);
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const maxW = 720;
      const scale = Math.min(1, maxW / Math.max(1, img.naturalWidth));
      canvas.width = Math.round(img.naturalWidth * scale);
      canvas.height = Math.round(img.naturalHeight * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setReady(true);
    };
    img.onerror = () => {
      if (!cancelled) setReady(false);
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [open, imageUrl]);

  const paintAt = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(x, y, brush / 2, 0, Math.PI * 2);
    ctx.fill();
  };

  const clearMask = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSubmit = () => {
    const canvas = canvasRef.current;
    if (!canvas || !String(prompt).trim()) return;
    const maskBase64 = canvas.toDataURL("image/png").split(",")[1];
    onSubmit?.({ prompt: String(prompt).trim(), maskBase64 });
  };

  const paperBg =
    theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff";

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={Zoom}
      transitionDuration={{ enter: 280, exit: 180 }}
      BackdropProps={{
        sx: {
          bgcolor: alpha("#0f172a", 0.5),
          backdropFilter: "blur(8px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          overflow: "hidden",
          border: "1px solid",
          borderColor: alpha(accent, 0.22),
          bgcolor: paperBg,
          backgroundImage: "none",
          boxShadow: `0 24px 64px ${alpha(accent, 0.18)}, 0 8px 24px rgba(15,23,42,0.12)`,
        },
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.35)})`,
        }}
      />
      <DialogContent
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1.5,
          backgroundImage: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, ${paperBg} 42%)`,
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.75} alignItems="flex-start">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: alpha(accent, 0.12),
                color: accent,
                border: "1px solid",
                borderColor: alpha(accent, 0.22),
                animation: open
                  ? `${pulseSoft} 2.4s ease-in-out infinite`
                  : "none",
              }}
            >
              <BrushOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0, pt: 0.2 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.45,
                  textTransform: "uppercase",
                  color: accent,
                }}
              >
                Moodboard · IA
              </Typography>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25 }}
              >
                Editar zona (inpaint)
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Pinta en blanco lo que quieres regenerar. El resto de la foto se
                conserva. La IA reescribe solo esa máscara con tu prompt.
              </Typography>
            </Box>
          </Stack>

          <TextField
            label="Qué generar en la zona"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            fullWidth
            disabled={busy}
            placeholder="Cuello más alto, cremallera metálica…"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: alpha(accent, 0.03),
              },
            }}
          />

          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ minWidth: 78 }}
            >
              Pincel {brush}px
            </Typography>
            <Slider
              size="small"
              min={8}
              max={80}
              value={brush}
              onChange={(_, v) => setBrush(Number(v))}
              disabled={busy}
              sx={{ color: accent, flex: 1 }}
            />
            <Button
              size="small"
              onClick={clearMask}
              disabled={busy}
              sx={{ fontWeight: 700, color: accent }}
            >
              Limpiar
            </Button>
          </Stack>

          <Box
            sx={{
              position: "relative",
              mx: "auto",
              width: "100%",
              border: "1px solid",
              borderColor: alpha(accent, 0.2),
              borderRadius: 2.5,
              overflow: "hidden",
              bgcolor: "#0b1220",
              minHeight: imageUrl ? undefined : 120,
              boxShadow: `inset 0 0 0 1px ${alpha("#fff", 0.04)}`,
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt=""
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: 420,
                  objectFit: "contain",
                  opacity: 0.9,
                }}
              />
            ) : null}
            <Box
              component="canvas"
              ref={canvasRef}
              onPointerDown={(e) => {
                drawingRef.current = true;
                e.currentTarget.setPointerCapture?.(e.pointerId);
                paintAt(e);
              }}
              onPointerMove={(e) => {
                if (!drawingRef.current) return;
                paintAt(e);
              }}
              onPointerUp={(e) => {
                drawingRef.current = false;
                try {
                  e.currentTarget.releasePointerCapture?.(e.pointerId);
                } catch {
                  /* ignore */
                }
              }}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                cursor: "crosshair",
                opacity: 0.5,
                touchAction: "none",
                mixBlendMode: "screen",
              }}
            />
          </Box>
          {!ready && imageUrl ? (
            <Typography variant="caption" color="text.secondary">
              Cargando imagen…
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ fontWeight: 700, color: accent }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={busy || !ready || String(prompt).trim().length < 2}
          onClick={handleSubmit}
          startIcon={
            busy ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={pagePrimaryButtonSx}
        >
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
